import { useCallback, useEffect, useRef, useState } from "react";
import { recordsApi } from "../../api";
import { MISSION_ACTIVITY_EVENT } from "../../missionEvents";
export interface RecordItem {
  key: string;
  kind:
    | "note"
    | "meal"
    | "exercise"
    | "health_bp"
    | "health_weight"
    | "health_sugar"
    | "health_hr"
    | "health_personal"
    | "study_log"
    | "study_task"
    | "study_quiz"
    | "study_project"
    | "study_exam"
    | "review"
    | "water";
  category?: string;
  can_edit?: boolean;
  can_delete?: boolean;
  hint?: string;
  id: number;
  subtype: string;
  title: string;
  content: string;
  day: string;
  time: string | null;
  label: string;
  dimensions: string[];
  data: Record<string, any>;
}
export function useRecordHistory(filters: Record<string, string>) {
  const query = JSON.stringify(filters);
  const generation = useRef(0),
    busy = useRef(false),
    timer = useRef<ReturnType<typeof setTimeout>>();
  const [rows, setRows] = useState<RecordItem[]>([]),
    [total, setTotal] = useState(0);
  const [cursor, setCursor] = useState<string | null>(null),
    [loading, setLoading] = useState(true),
    [error, setError] = useState("");
  const fetchPage = useCallback(
    async (next?: string) => {
      const request = ++generation.current;
      busy.current = true;
      setLoading(true);
      setError("");
      try {
        const response = await recordsApi.list({
          ...JSON.parse(query),
          limit: 30,
          ...(next ? { cursor: next } : {}),
        });
        if (request !== generation.current) return;
        setRows((previous) =>
          next
            ? [
                ...previous,
                ...response.data.results.filter(
                  (r: RecordItem) => !previous.some((p) => p.key === r.key),
                ),
              ]
            : response.data.results,
        );
        setCursor(response.data.next_cursor);
        setTotal(response.data.total);
      } catch (e: any) {
        if (request === generation.current)
          setError(e?.response?.data?.detail || "记录加载失败，请重试");
      } finally {
        if (request === generation.current) {
          busy.current = false;
          setLoading(false);
        }
      }
    },
    [query],
  );
  const reload = useCallback(() => {
    clearTimeout(timer.current);
    return fetchPage();
  }, [fetchPage]);
  useEffect(() => {
    const requests = generation;
    setRows([]);
    setCursor(null);
    setTotal(0);
    reload();
    const refresh = () => {
      clearTimeout(timer.current);
      timer.current = setTimeout(reload, 150);
    };
    window.addEventListener(MISSION_ACTIVITY_EVENT, refresh);
    return () => {
      requests.current++;
      busy.current = false;
      clearTimeout(timer.current);
      window.removeEventListener(MISSION_ACTIVITY_EVENT, refresh);
    };
  }, [reload]);
  return {
    rows,
    total,
    cursor,
    loading,
    error,
    reload,
    loadMore: () =>
      !busy.current && cursor ? fetchPage(cursor) : Promise.resolve(),
  };
}
